import { TextField, TextFieldProps, useTheme } from '@mui/material'
import React, { ChangeEvent } from 'react'
import { TFunction, useTranslation } from 'react-i18next'
import { FieldValidity } from '../Interfaces/FieldValidity'
import { useDateRangeValidity } from '../../hooks/useDateRangeValidity'
import { DesktopDatePicker as DatePicker } from '@mui/x-date-pickers'
import { dateToSlashString } from '../../utils/dateUtility'
import { format, isValid, parse } from 'date-fns'

export enum DateFieldVariant {
  RoleStartDate = 'roleStartDate',
  RoleEndDate = 'roleEndDate',
  FirstSemesterStartDate = 'firstSemesterStartDate',
  SecondSemesterStartDate = 'secondSemesterStartDate',
  EventStartDate = 'eventStartDate',
  EventEndDate = 'eventEndDate',
  EffectiveDate = 'effectiveDate',
  EndDate = 'endDate',
  EventsByDate = 'eventsByDate',
  DateOfBirth = 'dateOfBirth',
  // For DateField Test
  DateOfToday = 'dateOfToday',
  RegistrationCloseDate = 'registrationCloseDate',
  DateOfHighSchoolEntry = 'dateOfHighSchoolEntry',
  DateOfGraduation = 'dateOfGraduation',
  DateReceived = 'dateReceived',
  NotificationStartDate = 'notificationStartDate',
  NotificationEndDate = 'notificationEndDate',
}

const getLabelForVariant = (
  variant: DateFieldVariant,
  t: TFunction
): string => {
  switch (variant) {
    case DateFieldVariant.RoleStartDate:
      return t('DateField.Label.RoleStartDate', 'Role Start Date')
    case DateFieldVariant.RoleEndDate:
      return t('DateField.Label.RoleEndDate', 'Role End Date')
    case DateFieldVariant.FirstSemesterStartDate:
      return t(
        'DateField.Label.FirstSemesterStartDate',
        '1st Semester Start Date'
      )
    case DateFieldVariant.SecondSemesterStartDate:
      return t(
        'DateField.Label.SecondSemesterStartDate',
        '2nd Semester Start Date'
      )
    case DateFieldVariant.EventStartDate:
      return t('DateField.Label.EventStartDate', 'Event Start Date')
    case DateFieldVariant.EventEndDate:
      return t('DateField.Label.EventEndDate', 'Event End Date')
    case DateFieldVariant.EffectiveDate:
      return t('DateField.Label.EffectiveDate', 'Effective Date')
    case DateFieldVariant.EndDate:
      return t('DateField.Label.EndDate', 'End Date')
    case DateFieldVariant.EventsByDate:
      return t('DateField.Label.EventsByDate', 'Events By Date')
    case DateFieldVariant.DateOfBirth:
      return t('DateField.Label.DateOfBirth', 'Date of Birth')
    // For DateField Test
    case DateFieldVariant.DateOfToday:
      return t('DateField.Label.DateOfToday', 'Date of Today')
    case DateFieldVariant.RegistrationCloseDate:
      return t('DateField.Label.EarlyCloseDate', 'Early Close Date')
    case DateFieldVariant.DateOfHighSchoolEntry:
      return t(
        'DateField.Label.DateOfHighSchoolEntry',
        'Date of High School Entry'
      )
    case DateFieldVariant.DateOfGraduation:
      return t('DateField.Label.DateOfGraduation', 'Date of Graduation')
    case DateFieldVariant.DateReceived:
      return t('DateField.Label.DateReceived', 'Date Received')
    case DateFieldVariant.NotificationStartDate:
      return t('DateField.Label.NotificationStartDate', 'Start Date')
    case DateFieldVariant.NotificationEndDate:
      return t('DateField.Label.NotificationEndDate', 'End Date')
    // We should NEVER have this default because all variants are accounted for, but the return value requires it
    default:
      return t('DateField.Label.BadDate', 'Bad Date')
  }
}
export const formatDate = 'yyyy-MM-dd'
const isTest = process.env.NODE_ENV === 'test'
const variant = 'filled'

export type DateFieldProps = {
  minDate?: Date
  maxDate?: Date
  onChangeForParent: (
    event: React.ChangeEvent<HTMLInputElement>,
    value?: string,
    validity?: FieldValidity
  ) => void
  helperTextForParent?: React.ReactNode
  /**
   * Determines whether or not the field is invalid
   */
  error?: boolean
  /**
   * Determines id, label, and inputProps aria-label for a given field.
   */
  dateVariant: DateFieldVariant
  /**
   * Name of the field generally used to additionally index a property of state
   * for easy update. Explicitly defined so it can be provided by the user but
   * if not defined, defaults to label.
   */
  name?: string
  /**
   * Value of the field
   */
  value: string
  /**
   * Optionally allow the field to be fullWidth
   */
  fullWidth?: boolean
  /**
   * Determines if the label should shrink to properly show the value and have no overlap
   */
  shrink?: boolean
  /**
   * Optionally disable the field
   */
  disabled?: boolean
  /**
   * Optional override method for onKeyDown
   */
  onKeyDown?: (Event: React.KeyboardEvent<HTMLInputElement>) => void
  ref?: React.RefObject<React.ReactElement>

  hideLabel?: boolean
  /**
   * When this property is set to `true`, it enables the clear (reset)
   * button in the Datepicker, allowing users to remove any unwanted values.
   */
  clearable?: boolean
} & Partial<TextFieldProps>

const DateField: React.FC<DateFieldProps> = ({
  minDate = new Date(Date.UTC(new Date().getFullYear() - 2, 0, 1)),
  maxDate = new Date(Date.UTC(new Date().getFullYear() + 2, 11, 31)),
  onChangeForParent,
  helperTextForParent,
  error = false,
  dateVariant,
  shrink = true,
  fullWidth = true,
  disabled = false,
  onKeyDown,
  hideLabel,
  clearable,
  ...additionalProps
}) => {
  const { t } = useTranslation()
  const label = getLabelForVariant(dateVariant, t)
  const theme = useTheme()
  const { validateDateRange } = useDateRangeValidity({
    minDate,
    maxDate,
  })

  function onChange(e: React.ChangeEvent<HTMLInputElement>): void {
    if (!!e.target.value && e.target.value.length === 10) {
      const selectedDate = parse(e.target.value, formatDate, new Date())
      if (!validateDateRange(selectedDate)) {
        const validity: FieldValidity = {
          input: true,
          beforeMax: selectedDate <= maxDate,
          afterMin: selectedDate >= minDate,
        }
        onChangeForParent(e, e.target.value, validity)
      } else {
        const validity: FieldValidity = {
          input: true,
          beforeMax: true,
          afterMin: true,
        }
        onChangeForParent(e, e.target.value, validity)
      }
    } else {
      const validity: FieldValidity = {
        input: !!e.target.value,
        beforeMax: false,
        afterMin: true,
      }
      onChangeForParent(e, e.target.value, validity)
    }
  }

  const additionalPropsForDatePicker = {
    ...additionalProps,
    value:
      additionalProps.value === ''
        ? null
        : parse(additionalProps.value, formatDate, new Date()),
    type: undefined,
  }

  const handleDateChange = (newDate: Date | null) => {
    const isValidDate =
      newDate instanceof Date && !isNaN(newDate.getTime()) && isValid(newDate)

    onChange({
      target: {
        value: isValidDate ? format(newDate, formatDate) : '',
        name: additionalProps.name,
      },
    } as ChangeEvent<HTMLInputElement>)
  }

  const textField: TextFieldProps = {
    ...additionalPropsForDatePicker,
    error,
    FormHelperTextProps: { disabled: false },
    fullWidth,
    helperText: error ? helperTextForParent : '',
    InputLabelProps: { shrink },
    inputProps: {
      ...additionalPropsForDatePicker.inputProps,
      'aria-label': label,
      style: { textTransform: 'lowercase' },
    },
    /**
     * Make sure that any configuration or styles are available for this DatePicker
     */
    InputProps: { ...additionalProps.InputProps },
    onKeyDown,
    variant,
  }

  const onKeyChange = (e: React.KeyboardEvent<HTMLDivElement>) => {
    e.preventDefault()
  }

  return disabled && additionalProps.type === 'text' ? (
    <TextField
      {...{
        ...additionalProps,
        label: hideLabel ? '' : label,
        onChange,
        variant,
        error,
        helperText: error ? helperTextForParent : '',
      }}
      disabled={disabled}
      value={
        additionalPropsForDatePicker.value !== null
          ? dateToSlashString(additionalPropsForDatePicker.value, true)
          : ''
      }
    />
  ) : (
    <DatePicker
      // Makes it easier to test selectors where typing is not allowed.
      views={isTest ? ['year', 'month', 'day'] : undefined}
      disabled={disabled}
      label={hideLabel ? '' : label}
      onChange={handleDateChange}
      slotProps={{
        field: { clearable },
        layout: {
          sx: {
            '& .MuiTypography-root': {
              color: theme.palette.text.primary,
            },
          },
        },
        textField: {
          ...textField,
          onKeyDown: onKeyChange,
        },
      }}
      value={additionalPropsForDatePicker.value ?? new Date()}
    />
  )
}

export default DateField
